import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { GCBadgeSVG, SCBadgeSVG } from "@/components/CurrencyBadge";

interface CoinPackage {
  id: string;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number;
}

/** Unified purchase item type */
interface PurchaseItem {
  id: string | number;
  title: string;
  price: number;
  original_price?: number | null;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  type: 'coin_package' | 'scheduled_offer' | 'promo_offer' | 'special_offer';
}

interface RegularPackageCardProps {
  pkg: CoinPackage;
  index: number;
  onPurchase: (item: PurchaseItem) => void;
  getPackageIcon: (gcAmount: number, scAmount: number) => string;
  formatCoins: (amount: number) => string;
  formatPrice: (price: number) => string;
}

export const RegularPackageCard: React.FC<RegularPackageCardProps> = ({
  pkg,
  onPurchase,
  formatCoins,
  formatPrice
}) => {
  const totalGC = (Number(pkg.gc_amount) || 0) + (Number(pkg.gc_bonus_amount) || 0);
  const totalSC = (Number(pkg.sc_amount) || 0) + (Number(pkg.sc_bonus_amount) || 0);
  const hasDiscount = pkg.original_price && Number(pkg.original_price) > Number(pkg.price);

  // Determine package tier for accent color
  const getAccentColor = () => {
    const gcAmount = Number(pkg.gc_amount) || 0;
    if (gcAmount >= 1000000) return 'from-purple-500 to-pink-500';
    if (gcAmount >= 500000) return 'from-amber-500 to-orange-500';
    if (gcAmount >= 250000) return 'from-yellow-500 to-amber-500';
    if (gcAmount >= 100000) return 'from-blue-500 to-cyan-500';
    if (gcAmount >= 50000) return 'from-green-500 to-emerald-500';
    return 'from-slate-400 to-slate-500';
  };

  const accentColor = getAccentColor();

  return (
    <Card className={cn(
      "relative overflow-hidden transition-all duration-300 group",
      "bg-card",
      "border border-border hover:border-border/80",
      "shadow-md hover:shadow-lg"
    )}>
      {/* Subtle top accent line */}
      <div className={cn(
        "absolute top-0 left-0 right-0 h-0.5 opacity-60 group-hover:opacity-100 transition-opacity",
        `bg-gradient-to-r ${accentColor}`
      )} />

      <CardContent className="p-4">
        {/* Currency display with badges */}
        <div className="flex flex-col items-center justify-center mb-3 min-h-[3.25rem]">
          <div className="grid grid-cols-[auto_auto_1fr] gap-x-1.5 gap-y-1 items-center justify-items-start w-fit">
            {/* GC row */}
            {totalSC > 0 && <span className="text-slate-600 invisible">+</span>}
            <GCBadgeSVG size={20} />
            <span className="text-base font-black text-amber-300">
              {formatCoins(totalGC)}
            </span>
            
            {/* SC row */}
            {totalSC > 0 && (
              <>
                <span className="text-slate-600">+</span>
                <SCBadgeSVG size={20} />
                <span className="text-sm font-bold text-green-400">
                  {formatCoins(totalSC)} <span className="text-green-400/70 text-xs">FREE</span>
                </span>
              </>
            )}
          </div>
        </div>
        
        <Button 
          onClick={() => onPurchase({
            id: pkg.id,
            title: pkg.title,
            price: pkg.price,
            original_price: pkg.original_price,
            gc_amount: Number(pkg.gc_amount) || 0,
            sc_amount: Number(pkg.sc_amount) || 0,
            gc_bonus_amount: Number(pkg.gc_bonus_amount) || 0,
            sc_bonus_amount: Number(pkg.sc_bonus_amount) || 0,
            type: 'coin_package',
          })}
          className={cn(
            "w-full font-bold px-4 py-2.5 rounded-lg text-sm h-10",
            "transition-all duration-200 relative overflow-hidden group/btn",
            "bg-muted",
            "hover:bg-gradient-to-r hover:from-amber-600 hover:via-amber-500 hover:to-amber-600",
            "text-foreground hover:text-white shadow-md hover:shadow-lg border border-border hover:border-amber-500/50"
          )}
        >
          <span className="relative z-10 flex items-center justify-center gap-1.5">
            {hasDiscount && (
              <span className="line-through text-white/40 text-xs">
                {formatPrice(pkg.original_price)}
              </span>
            )}
            <span className="font-black">{formatPrice(pkg.price)}</span>
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-500" />
        </Button>
      </CardContent>
    </Card>
  );
};